import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

import { doSaveTableColHeader } from '../../../builder/reducers/builder';
import { InputWrapper, TableHeaderSpan } from './table_field.styled';
import { htmlToPlainText } from '../../../../shared/utils';
import { selectCtrlIdLabelMap } from '../../selectors';
import { ControlPropType } from '../../ctrl_proptype';

const ColumnHead = ({ th, ctrl, previewMode, pgIndex, headerValue }) => {
  const node = useRef();
  const dispatch = useDispatch();
  const [toggle, setToggle] = useState(false);
  const ctrlIdLabelMap = useSelector(selectCtrlIdLabelMap);
  const [columnTitle, setColumnTitle] = useState(headerValue);
  const columnHeader = ctrlIdLabelMap?.[ctrl.internal_id]?.[th - 1];

  useEffect(() => setColumnTitle(headerValue), [headerValue]);

  const saveColHeader = (value) => {
    dispatch(doSaveTableColHeader({ pgIndex, tableId: ctrl.internal_id, colIndex: th - 1, value }));
  };

  const updateHeaderVal = useRef(
    debounce((value) => {
      saveColHeader(value);
    }, 300)
  );

  const handleValueChange = (e) => {
    const newValue = htmlToPlainText(e.target.value);
    setColumnTitle(newValue);
    updateHeaderVal.current(newValue);
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    setToggle(!toggle);
  };

  const handleOutsideClick = (e) => {
    e.stopPropagation();
    if (node && !node?.current?.contains(e.target) && toggle) setToggle(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  });

  // when enter or tab key is pressed, input field shouldn't show anymore.
  const handleKeyPress = (e) => {
    if (e.charCode === 13 || e.which === 27) handleToggle();
  };

  return (
    <div ref={node} role="button" tabIndex={0}>
      {toggle && !previewMode ? (
        <InputWrapper
          type="text"
          value={columnTitle}
          onBlur={handleToggle}
          onChange={handleValueChange}
          onClick={(e) => e.stopPropagation()}
          id={`input-${ctrl.internal_id}-${th}`}
        />
      ) : (
        <TableHeaderSpan
          tabIndex="0"
          role="button"
          onClick={handleToggle}
          onKeyDown={handleKeyPress}
          id={`colhd-${ctrl.internal_id}-${th}`}
          requiredStar={ctrl.table_props[th - 1]?.required}
          dangerouslySetInnerHTML={{ __html: columnHeader || columnTitle || 'Rename column' }}
        />
      )}
    </div>
  );
};

export default ColumnHead;

ColumnHead.defaultProps = {
  th: null,
  previewMode: false,
};

ColumnHead.propTypes = {
  th: PropTypes.number,
  previewMode: PropTypes.bool,
  pgIndex: PropTypes.number.isRequired,
  headerValue: PropTypes.string.isRequired,
  ctrl: PropTypes.shape(ControlPropType).isRequired,
};
